import React, { useState } from "react";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import ErrorMessage from "../../../core/common/ErrorMessage";
import handleClearError from "../../../core/methods/handleClearError";
import CaptchaWidget from "../../../core/common/ReactCaptcha";
import useApi from "../../../core/customhook/useApi";
import webservice_url from "../../../core/webservice/Webservice_url";
import { useAuth } from "../../../core/context/useAuthContext";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import getDynamicRedirect from "../../../core/utils/getDynamicRedirect";

type PasswordField = "password";

interface LoginType {
  email: string;
  password: string;
  captcha: string | null;
}

const initial: LoginType = {
  email: "",
  password: "",
  captcha: "",
};

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [errors, setErrors] = useState<Record<string, string> | null>(
    null,
  );

  const [captchaCode, setCaptchaCode] = useState<number | string | null>(
    "",
  );

  const [formData, setFormData] = useState<LoginType>(initial);

  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
  });

  // Login API
  const {
    callApi: loginAPI,
    loading: loading_login,
  } = useApi<any>(webservice_url.login, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,

    // Important: wrong credentials must not show Session Expired.
    handleUnauthorized: false,
  });

  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: name === "email" ? value.trim() : value,
    }));

    handleClearError(setErrors, name);
  };

  const ValidateLoginFields = () => {
    const newErrors: Record<string, string> = {};

    const { email, password, captcha } = formData;

    if (!email?.trim()) {
      newErrors.email = "Email is required";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)
    ) {
      newErrors.email = "Enter valid email address";
    }

    if (!password?.trim()) {
      newErrors.password = "Password is required";
    }

    // Validate captcha after email and password are valid.
    if (
      !!email?.trim() &&
      !!password?.trim() &&
      !newErrors.email &&
      !newErrors.password
    ) {
      if (!captcha?.trim()) {
        newErrors.captcha = "Captcha is required";
      } else if (String(captcha) !== String(captchaCode)) {
        newErrors.captcha = "Invalid captcha";
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async () => {
    if (!ValidateLoginFields()) {
      return;
    }

    const payload = {
      email: formData.email.trim(),
      password: formData.password,
    };

    try {
      const response = await loginAPI(payload);

      if (response?.success) {
        login(response.data);

        await Swal.fire({
          icon: "success",
          title: "Login Successful",
          text: response.message || "Welcome back!",
          timer: 1500,
          showConfirmButton: true,
        });

        navigate(
          getDynamicRedirect(response.data?.profile_name),
          { replace: true },
        );
      } else {
        // Handle unsuccessful responses that resolve without throwing.
        toast.error(
          response?.error ||
            response?.message ||
            "Unable to log in. Please try again.",
        );
      }
    } catch (error: any) {
      console.error("Login API error:", error);

      // Display the actual backend error message.
      const errMsg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Something went wrong. Please try again.";

      toast.error(errMsg);
    }
  };

  return (
    <div className="container-fluid vh-100 overflow-hidden p-0">
      <div className="row h-100 g-0">
        {/* Left panel - image/branding */}
        <div className="col-lg-7 d-none d-lg-flex align-items-center justify-content-center position-relative">
          <div
            className="position-absolute top-0 start-0 h-100 w-100 opacity-25"
            style={{
              border: "none",
              background: "black",
            }}
          />

          <ImageWithBasePath
            src="assets/img/login_image.png"
            alt="Img"
            className="object-fit-cover w-100 h-100 position-relative"
          />
        </div>

        {/* Right panel - login form */}
        <div className="col-12 col-lg-5 h-100 overflow-auto d-flex align-items-center justify-content-center">
          <div className="col-11 col-sm-8 col-md-6 col-lg-10 py-4">
            <div>
              {/* Logo */}
              <div className="mx-auto mt-3" style={{ width: 160 }}>
                <ImageWithBasePath
                  src="/assets/img/logo/AupTech.svg"
                  className="img-fluid"
                  alt="Logo"
                />
              </div>

              {/* Login content */}
              <div>
                <div className="text-center mt-1 mb-3">
                  <h2 className="mb-2">Sign In</h2>
                  <p className="mb-0">
                    Please enter your details to sign in
                  </p>
                </div>

                <div>
                  {/* Email */}
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label">
                      Email Address
                    </label>

                    <div className="input-group">
                      <input
                        id="email"
                        type="email"
                        className="form-control border-end-0"
                        name="email"
                        value={formData.email ?? ""}
                        placeholder="Enter Email Address"
                        onChange={handleInputChange}
                        disabled={loading_login}
                      />

                      <span className="input-group-text border-start-0 pe-3">
                        <i className="ti ti-mail" />
                      </span>
                    </div>

                    <ErrorMessage
                      message={errors?.email}
                      classStyle="mt-1"
                    />
                  </div>

                  {/* Password */}
                  <div className="mb-3">
                    <label htmlFor="password" className="form-label">
                      Password
                    </label>

                    <div className="pass-group">
                      <input
                        id="password"
                        type={
                          passwordVisibility.password
                            ? "text"
                            : "password"
                        }
                        className="pass-input form-control"
                        name="password"
                        value={formData.password ?? ""}
                        placeholder="Enter Password"
                        onChange={handleInputChange}
                        disabled={loading_login}
                      />

                      <span
                        className={`ti toggle-passwords ${
                          passwordVisibility.password
                            ? "ti-eye"
                            : "ti-eye-off"
                        }`}
                        onClick={() =>
                          togglePasswordVisibility("password")
                        }
                      />
                    </div>

                    <ErrorMessage
                      message={errors?.password}
                      classStyle="mt-1"
                    />
                  </div>

                  {/* Forgot password */}
                  <div className="d-flex align-items-center justify-content-end mb-1">
                    <div className="text-end">
                      <button
                        type="button"
                        className="border-0 p-0 mb-2 text-decoration-none link-danger"
                        disabled={loading_login}
                        onClick={() =>
                          navigate(all_routes.forgotPassword)
                        }
                      >
                        Forgot Password?
                      </button>
                    </div>
                  </div>

                  {/* Captcha */}
                  <div className="mb-3">
                    <CaptchaWidget
                      disabled={loading_login}
                      value={String(captchaCode ?? "")}
                      onChange={(code) => {
                        setCaptchaCode(code);

                        setFormData((prev) => ({
                          ...prev,
                          captcha: "",
                        }));
                      }}
                    />

                    <label htmlFor="captcha" className="form-label">
                      Enter Captcha
                    </label>

                    <div className="input-group mb-1">
                      <input
                        id="captcha"
                        type="text"
                        name="captcha"
                        value={formData.captcha || ""}
                        className="form-control"
                        placeholder="Enter captcha"
                        onChange={handleInputChange}
                        disabled={loading_login}
                      />

                      <button
                        type="button"
                        className="btn btn-dark"
                        disabled={loading_login}
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            captcha: "",
                          }))
                        }
                      >
                        <i className="ti ti-refresh" />
                      </button>
                    </div>

                    <ErrorMessage message={errors?.captcha} />
                  </div>

                  {/* Login button */}
                  <div className="mb-3 mt-0">
                    <button
                      type="button"
                      className="btn btn-primary w-100 py-2 fs-17"
                      onClick={handleLogin}
                      disabled={loading_login}
                    >
                      {loading_login ? "Signing In..." : "Sign In"}
                    </button>
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-3 pb-4 text-center">
                  <p className="mb-0 text-gray-9">
                    © {new Date().getFullYear()} Aupertz. All rights reserved.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;


// import React, { useState } from "react";
// import ImageWithBasePath from "../../../core/common/imageWithBasePath";
// import { useNavigate } from "react-router-dom";
// import { all_routes } from "../../router/all_routes";
// import ErrorMessage from "../../../core/common/ErrorMessage";
// import handleClearError from "../../../core/methods/handleClearError";
// import CaptchaWidget from "../../../core/common/ReactCaptcha";
// import useApi from "../../../core/customhook/useApi";
// import webservice_url from "../../../core/webservice/Webservice_url";
// import { useAuth } from "../../../core/context/useAuthContext";
// import Swal from "sweetalert2";
// import { toast } from "react-toastify";
// import getDynamicRedirect from "../../../core/utils/getDynamicRedirect";
// type PasswordField = "password";

// interface LoginType {
//   email: string;
//   password: string;
//   captcha: string | null;
// } 
// const initial = {
//   email: "",
//   password: "",
//   captcha: "",
// };

// const Login = () => { 
//   const navigate = useNavigate();
//   const { login } = useAuth();

//   const [errors, setErrors] = useState<Record<string, string> | null>(null);
//   const [captchaCode, setCaptchaCode] = useState<number | string | null>("");
//   const [formData, setFormData] = useState<LoginType>(initial);
//   const [passwordVisibility, setPasswordVisibility] = useState({
//     password: false,
//   });

//   // API
//   const { callApi: loginAPI, loading: loading_login } = useApi<any>(webservice_url.login, {
//     method: "POST",
//     autoCall: false,
//     handleGlobalError: false,
//   });
 
//   const togglePasswordVisibility = (field: PasswordField) => {
//     setPasswordVisibility((prevState) => ({
//       ...prevState,
//       [field]: !prevState[field],
//     }));
//   };
 
//   const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
//     const { name, value } = e.target;

//     setFormData((prev) => ({
//       ...prev,
//       [name]: name === "email" ? value?.trim() : value,
//     }));

//     handleClearError(setErrors, name);
//   };

//   const ValidateLoginFields = () => {
//     let newErrors: any = {};

//     const { email, password, captcha } = formData;

//     if (!email?.trim()) {
//       newErrors.email = "Email is required";
//     } else if (
//       !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
//     ) {
//       newErrors.email = "Enter valid email address";
//     }

//     if (!password?.trim()) {
//       newErrors.password = "Password is required";
//     }

//     // Captcha Validation
//     if (!!email && !!password && !newErrors?.email && !newErrors?.password) {
//       if (!captcha?.trim()) {
//         newErrors.captcha = "Captcha is required";
//       } else if (String(captcha) !== String(captchaCode)) {
//         newErrors.captcha = "Invalid captcha";
//       }
//     }
//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const handleLogin = async () => {
//     if (!ValidateLoginFields()) return;

//     const payload = {
//       email: formData.email.trim(),
//       password: formData.password,
//     };

//     try {
//       const response = await loginAPI(payload); 
//       if (response?.success) {
//         login(response.data);
//         await Swal.fire({
//           icon: "success",
//           title: "Login Successful",
//           text: response.message || "Welcome back!",
//           timer: 1500,
//           showConfirmButton: true,
//         }); 
//         navigate(getDynamicRedirect(response.data?.profile_name), { replace: true });
//       }
//     } catch (error: any) {
//       console.error(error);
//       const errMsg = error?.response?.data?.error || "Something went wrong";
//       toast.error(errMsg);
//     }
//   };

//   return (
//     <div className="container-fluid vh-100 overflow-hidden p-0">
//       <div className="row h-100 g-0">

//         {/* Left panel - image/branding - only visible on lg+ */}
//         <div className="col-lg-7 d-none d-lg-flex align-items-center justify-content-center position-relative">
//           <div className="position-absolute top-0 start-0 h-100 w-100 opacity-25" style={{ border: "none", background: "black" }}></div>
//           <ImageWithBasePath
//             src="assets/img/login_image.png"
//             alt="Img"
//             className="object-fit-cover w-100 h-100 position-relative"
//           />
//         </div>

//         {/* Right panel - login form - full width below lg, col-lg-5 on lg+ */}
//         <div className="col-12 col-lg-5 h-100 overflow-auto d-flex align-items-center justify-content-center ">
//           <div className="col-11 col-sm-8 col-md-6 col-lg-10 py4 ">
//             {/* login content */}
//             <div>
//               {/* logo */}
//               <div className="mx-auto mt-3" style={{ width: 160 }}>
//                 <ImageWithBasePath
//                   src="/assets/img/logo/AupTech.svg"
//                   className="img-fluid"
//                   alt="Logo"
//                 />
//               </div>
//               {/* form card */}
//               <div>
//                 <div className="text-center mt-1 mb-3">
//                   <h2 className="mb-2">Sign In</h2>
//                   <p className="mb-0">Please enter your details to sign in</p>
//                 </div>
//                 <div>
//                   <div className="mb-3">
//                     <label htmlFor="email" className="form-label">
//                       Email Address
//                     </label>
//                     <div className="input-group">
//                       <input
//                         type="email"
//                         className="form-control border-end-0"
//                         name="email"
//                         value={formData?.email ?? ""}
//                         placeholder="Enter Email Address"
//                         onChange={handleInputChange}
//                         disabled={loading_login}
//                       />
//                       <span className="input-group-text border-start-0 pe-3">
//                         <i className="ti ti-mail" />
//                       </span>
//                     </div>
//                     <ErrorMessage message={errors?.email} classStyle="mt-1" />
//                   </div>

//                   <div className="mb-3">
//                     <label htmlFor="password" className="form-label">
//                       Password
//                     </label>
//                     <div className="pass-group ">
//                       <input
//                         type={passwordVisibility.password ? "text" : "password"}
//                         className="pass-input form-control"
//                         name="password"
//                         value={formData?.password ?? ""}
//                         placeholder="Enter Password"
//                         onChange={handleInputChange}
//                         disabled={loading_login}
//                       />
//                       <span
//                         className={`ti toggle-passwords ${passwordVisibility.password ? "ti-eye" : "ti-eye-off"
//                           }`}
//                         onClick={() => togglePasswordVisibility("password")}
//                       ></span>
//                     </div>
//                     <ErrorMessage message={errors?.password} classStyle="mt-1" />
//                   </div>

//                   <div className="d-flex align-items-center justify-content-end mb-1">
//                     <div className="text-end">
//                       <button
//                         type="button"
//                         className="border-0 p-0 mb-2 text-decoration-none link-danger"
//                         disabled={loading_login}
//                         onClick={() => {
//                           navigate(all_routes.forgotPassword);
//                         }}
//                       >
//                         Forgot Password?
//                       </button>
//                     </div>
//                   </div>

//                   {/* Captcha */}
//                   <div className="mb-3">
//                     <CaptchaWidget
//                       disabled={loading_login}
//                       value={String(captchaCode) ?? ""}
//                       onChange={(code) => {
//                         setCaptchaCode(code);
//                         setFormData((prev) => ({ ...prev, captcha: "" }));
//                       }}
//                     />

//                     <label htmlFor="captcha" className="form-label">Enter Captcha</label>
//                     <div className="input-group mb-1">
//                       <input
//                         type="text"
//                         name="captcha"
//                         value={formData?.captcha || ""}
//                         className="form-control"
//                         placeholder="Enter captcha"
//                         onChange={handleInputChange}
//                       />
//                       <button
//                         type="button"
//                         className="btn btn-dark"
//                         disabled={loading_login}
//                         onClick={() =>
//                           setFormData((prev) => ({ ...prev, captcha: "" }))
//                         }
//                       >
//                         <i className="ti ti-refresh"></i>
//                       </button>
//                     </div>
//                     <ErrorMessage message={errors?.captcha} />
//                   </div>

//                   {/* --- login button --- */}
//                   <div className="mb-3 mt-0">
//                     <button
//                       type="button"
//                       className="btn btn-primary w-100 py-2 fs-17"
//                       onClick={handleLogin}
//                       disabled={loading_login}
//                     >
//                       {loading_login ? "Signing In..." : "Sign In"}
//                     </button>
//                   </div>
//                 </div>
//                 <div className="mt-3 pb-4 text-center">
//                   <p className="mb-0 text-gray-9">
//                     © {new Date().getFullYear()} Aupertz. All rights reserved.
//                   </p>
//                 </div>
//               </div>
//             </div>
//           </div>
//           {/* end login content */}
//         </div>
//       </div>

//     </div>
//   );
// };

// export default Login; 